import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { weekDaysDataSource } from 'infrastructure/data-sources/week-days';
import {
  convertDateToTime,
  convertDateToTimeZone,
  convertTime,
  convertTimeToDate,
} from 'infrastructure/functions';
import ClockPhoneIcon from 'components/atoms/icons/clockPhoneIcon';
import { normalizeTimezone } from 'utils';
import { Availability } from 'infrastructure/classes/availability';

import s from './styles.module.scss';

import type { InteractiveTimeItem } from 'infrastructure/hooks/interactiveTimeDashboard/types';

interface IProps {
  availabilities: InteractiveTimeItem['contactAvailabilities'];
  timezone: string;
}

const ContactAvailability: React.FC<IProps> = (props) => {
  const { availabilities, timezone } = props;

  const { t } = useTranslation();

  const weekDays = weekDaysDataSource();
  const [currentPatientDate, setCurrentPatientDate] = useState(
    convertDateToTimeZone(new Date(), timezone),
  );

  const anytime = !availabilities.length;

  const isAvailable = useMemo(() => {
    return Availability.isAvailable(
      availabilities.map(
        (el, i) =>
          new Availability({
            day: el.day,
            startTime: el.startTime,
            endTime: el.endTime,
            guid: `${i}`,
            patientGuid: `${i}`,
          }),
      ),
      timezone,
    );
  }, [availabilities, currentPatientDate, timezone]);

  const availableText = isAvailable
    ? t('labels.available')
    : t('labels.notAvailable');

  const statusText = anytime ? t('labels.anytime') : availableText;

  const timeClassNames = classNames(s.time, {
    [s.green]: isAvailable || anytime,
    [s.red]: !isAvailable && !anytime,
  });
  const bodyClassNames = classNames(s.body, {
    [s.anytime]: anytime,
  });

  const groupedTimes = useMemo(() => {
    if (availabilities.length === 0) {
      return weekDays.map((day) => ({ ...day, times: [t('labels.anytime')] }));
    }
    return weekDays
      .map((day, index) => {
        const times = availabilities.filter((d) => d.day === index);
        if (times.length === 0) {
          return {
            ...day,
            times: [
              availabilities.length
                ? t('labels.notAvailable')
                : t('labels.anytime'),
            ],
          };
        }
        return {
          ...day,
          times: times
            .filter((d) => d.day === index)
            .sort((a, b) => {
              const at = convertTimeToDate(a.startTime).getTime();
              const bt = convertTimeToDate(b.endTime).getTime();
              return at - bt;
            })
            .map(({ startTime, endTime }) => {
              return `${convertTime(startTime)} - ${convertTime(endTime)};`;
            }),
        };
      })
      .filter((el) => el.times.length);
  }, [availabilities, weekDays]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentPatientDate(convertDateToTimeZone(new Date(), timezone));
    }, 60000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  return (
    <div className={bodyClassNames}>
      <div className={s.header}>
        <div className={s.icon}>
          <ClockPhoneIcon />
        </div>
        <span className={timeClassNames}>
          {`${convertDateToTime(currentPatientDate)} (${normalizeTimezone(
            timezone,
          )}) – ${statusText}`}
        </span>
      </div>
      <ul className={s.list}>
        {groupedTimes.map((el, i) => (
          <li key={i}>
            <div className={s.day}>
              <span>{el.name}</span>
            </div>
            <div className={s.times}>
              {el.times.map((time, e) => (
                <div key={e} className={s.time}>
                  {time === t('labels.notAvailable') ? (
                    <span className={s['not-available']}>
                      {t('labels.notAvailable')}
                    </span>
                  ) : (
                    time
                  )}
                </div>
              ))}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ContactAvailability;
